import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import type {State} from '@bitstillery/mithril'
import {format_month_day_year} from '@bitstillery/common/lib/format'
import {required, reset_validation, validation} from '@bitstillery/common/lib/validation'
import {Button, FieldTextArea, Icon, RowActionDelete} from '@bitstillery/common/components'
import m from '@bitstillery/mithril'

import type {GetRelationLogBookEntryResponse} from '@bitstillery/common/api/fact2server'

export const LogType = {
    CONTACT_UPDATE: 'CONTACT_UPDATE',
    MESSAGE: 'MESSAGE',
} as const

interface SupplierLogbookAttrs {
    mode?: 'lead' | 'supplier'
    artkey: string | number
    messages: (GetRelationLogBookEntryResponse | LogEntry)[]
    onsave: (message: string) => Promise<void>
    ondelete?: (message: GetRelationLogBookEntryResponse | LogEntry) => void
}

interface LogEntry {
    log_type: string
    created_on: string
    created_by: {
        profile: {
            name: string
        }
    } | null
    message: string
}

/** The logbook endpoint names the author flat; a LogEntry nests it in its creator's profile. */
function author_name(message: GetRelationLogBookEntryResponse | LogEntry): string | undefined {
    if ('created_by' in message && message.created_by) {
        return message.created_by.profile.name
    }
    return 'profile_name' in message ? message.profile_name : undefined
}

interface TimelineData {
    loading: boolean
    log_entries: LogEntry[]
    log_type: (typeof LogType)[keyof typeof LogType]
    message: string
}

export class Timeline extends MithrilComponent<SupplierLogbookAttrs> {
    data: State<TimelineData> = state(
        {
            loading: false,
            log_entries: [],
            log_type: LogType.MESSAGE,
            message: '',
        },
        'timeline-data',
    )

    $v = {
        message: validation(this.data.$message, required()),
    }

    view(vnode: m.Vnode<SupplierLogbookAttrs>): m.Children {
        return (
            <div className='c-timeline'>
                <div className='timeline'>
                    <div className='now'>Now</div>
                    {vnode.attrs.messages.map((message) => (
                        <div className='timeline-item'>
                            <div class='timeline-message'>
                                <div className='message-header'>
                                    <div className='message-header-left'>
                                        <Icon
                                            name={message.log_type === LogType.CONTACT_UPDATE ? 'systemMessage' : 'profile'}
                                            className='entry-type-icon'
                                            type='unset'
                                        />
                                        {author_name(message)}
                                        &nbsp;-
                                        <div className='entry-date'>{format_month_day_year(message.created_on)}</div>
                                    </div>
                                    <div className='message-header-right'>
                                        {vnode.attrs.ondelete && (
                                            <RowActionDelete
                                                needs_confirmation={true}
                                                row={message}
                                                row_delete={() => vnode.attrs.ondelete?.(message)}
                                                type='info'
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className='message-content'>{message.message}</div>
                            </div>
                            <Icon name='circle_outline' className='timeline-icon' size='s' type='unset' />
                        </div>
                    ))}
                    <div className='timeline-item'>
                        <div className='timeline-message'>
                            <div className='message-header'>
                                <div className='message-header-left'>
                                    <Icon name='profile' className='entry-type-icon' type='unset' />
                                    New Memo message
                                </div>
                            </div>
                            <div className='message-content'>
                                <FieldTextArea
                                    help='Enter customer context to share with colleagues'
                                    model={this.data.$message}
                                    validation={this.$v.message}
                                />
                                <Button
                                    disabled={this.data.loading || Object.values(this.$v).filter((i) => i._invalid).length > 0}
                                    icon='save'
                                    className='btn-submit'
                                    text='Add Memo'
                                    type='info'
                                    onclick={async () => {
                                        this.data.loading = true
                                        await vnode.attrs.onsave(this.data.message)
                                        reset_validation(this.$v)
                                        this.data.message = ''
                                        this.data.loading = false
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}
